import { onScopeDispose, ref, shallowRef, watch } from 'vue';

/**
 * Overflow-aware paging for a horizontal scroll rail.
 *
 * The photo rails hide their scrollbar and rely on touch-swipe, which is
 * near-invisible on the 4K vertical kiosk once a person has more portraits
 * than fit on screen. This exposes `canScrollLeft/Right` (so the arrows show
 * only when there is somewhere to go) and `page()` to jump a viewport.
 */
export function useRailScroll() {
  const railRef = shallowRef<HTMLElement | null>(null);
  const canScrollLeft = ref(false);
  const canScrollRight = ref(false);

  let observedEl: HTMLElement | null = null;
  let resizeObserver: ResizeObserver | null = null;

  // 1px slack absorbs sub-pixel rounding so the right arrow doesn't flicker
  // at the exact end of the scroll range.
  function update(): void {
    const el = railRef.value;
    if (!el) {
      canScrollLeft.value = false;
      canScrollRight.value = false;
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    canScrollLeft.value = el.scrollLeft > 1;
    canScrollRight.value = el.scrollLeft < maxScroll - 1;
  }

  // Page by ~80% of the viewport so one column of context carries across the
  // jump instead of a full-width leap that loses the visitor's place.
  function page(direction: 1 | -1): void {
    const el = railRef.value;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  }

  function detach(): void {
    observedEl?.removeEventListener('scroll', update);
    observedEl = null;
    resizeObserver?.disconnect();
    resizeObserver = null;
  }

  // Watch the template ref instead of onMounted: the group rail is v-if'd, so
  // its element can appear (or vanish) after the component mounts — an
  // onMounted-only attach would silently never wire a late-appearing rail.
  // flush: 'post' runs after the DOM settles so first-measure sees real sizes.
  watch(
    railRef,
    (el) => {
      detach();
      if (!el) {
        update();
        return;
      }
      observedEl = el;
      el.addEventListener('scroll', update, { passive: true });
      // jsdom has no ResizeObserver; the guard keeps any future component
      // test from crashing at mount (arrows just stay static there).
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(el);
        // Also observe the track: adding/removing cells grows scrollWidth
        // without resizing the rail's own box, so a rail-only observer would
        // never learn the content became scrollable.
        const track = el.firstElementChild;
        if (track) resizeObserver.observe(track);
      }
      update();
    },
    { flush: 'post' }
  );

  onScopeDispose(detach);

  // A function ref, because a string ref in a v-for collects an array.
  function bindRail(el: unknown): void {
    railRef.value = el instanceof HTMLElement ? el : null;
  }

  return { railRef, bindRail, canScrollLeft, canScrollRight, page };
}
